/**
 * TaxCollectorPicker — Phase 4 (Feb 2026).
 *
 * Reusable type-ahead picker that lets a processor link an order
 * jurisdiction's payee (current or delinquent) to a row in the
 * `tax_collectors` master-data table (Admin → Tax Collectors).
 *
 * SCOPE (initial release):
 *   • PA-only — caller is expected to gate visibility on
 *     `state === 'PA'`. The component itself does NOT short-circuit
 *     on state so it can be reused later without rewrites.
 *   • Supports `taxPeriod = 'current' | 'delinquent'`.
 *   • Backwards compatible: when the user picks "Type a custom
 *     payee" or unlinks, FK is set to null and the parent retains
 *     editable free-text inputs exactly as before.
 *
 * PERSISTENCE CONTRACT:
 *   Picker emits a single `onLinkMasterData(collector)` call that
 *   carries all 4 fields. Parent is expected to write atomically:
 *     {
 *       current_collector_id            (or delinquent_collector_id),
 *       make_checks_payable_to          (or delinquent_payable_to_name),
 *       tax_collector_phone             (or delinquent_payable_to_phone),
 *       tax_collector_physical_address  (or delinquent_payable_to_address),
 *     }
 *
 *   `onUnlink()` clears the FK only; strings stay (now editable).
 *
 * UX RULES (locked):
 *   • When linked → trigger shows pill `Linked · Master Data` (green
 *     for current, blue for delinquent) and the type chip; payee
 *     name/phone/address are read-only at the parent layer.
 *   • When not linked → trigger is a search input; user can type to
 *     filter. Footer always shows "Type a custom payee" (preserves
 *     today's free-text path) and a deep-link to /admin/jurisdictions/tax-collectors.
 *   • The assignment-suggested collector is marked with a ★ pill so
 *     the processor sees it first.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Lock, Pencil, Plus, ExternalLink, Star, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { listTaxCollectors, getTaxCollector, resolveCollectorGuidance } from '../../utils/taxCollectorsApi';

const DEBOUNCE_MS = 300;

const TYPE_LABEL = {
  county_treasurer:      'County Treasurer',
  county_tax_claim:      'PA Tax Claim',
  tax_claim_bureau:      'PA Tax Claim',
  municipal_collector:   'Municipal',
  school_collector:      'School',
  third_party_agency:    'Third-party',
  internal_revenue_dept: 'Revenue Dept',
  other:                 'Other',
};

// Jul 2026 — AMBIGUOUS-ASSIGNMENT-GUARD (Carbon County UAT). When the
// collector is assignment-resolved, the chip must state WHAT TAX the
// matched assignment covers (its tax_category_code), NOT the collector
// master record's directory type. A township "municipal_collector"
// formally assigned to collect County Tax must read "County Tax".
const CATEGORY_LABEL = {
  county:    'County Tax',
  municipal: 'Municipal Tax',
  school:    'School Tax',
  tax_claim: 'Tax Claim',
};

const TaxCollectorPicker = ({
  // identity
  state,                     // e.g. 'PA' — scopes search; null disables fetch
  taxPeriod = 'current',     // 'current' | 'delinquent'
  jurisdictionId = null,     // for ★ assignment-match pill
  taxCategoryCode = null,    // optional — narrows the assignment resolve
  // Feb 7, 2026 — Context fallback. When the parcel row has no
  // `jurisdiction_id` FK saved (legacy data drift), supply these so
  // the backend resolver can still find the master assignment via
  // canonical (state + name [+ type]) match.
  jurisdictionName = null,
  jurisdictionType = null,
  // Feb 2026 (Option C) — Municipality scope hint. When the parcel
  // sits inside a township/borough/city with its own scoped collector
  // override (e.g. Salem Township under Western Wayne School), passing
  // its jurisdiction id here makes the resolver prefer that scoped
  // assignment over the SD-level catch-all. Silently ignored when the
  // school district (or other parent) has no matching scoped row.
  municipalityJurisdictionId = null,

  // value
  collectorId = null,        // FK; null when custom
  payeeName = '',            // string for the trigger's display text when linked / custom

  // events
  onLinkMasterData,          // (collector, ctx?) => void — see contract above
  onUnlink,                  // () => void
  onPickCustom,              // () => void — caller flips its UI back to free-text inputs

  // UX
  disabled = false,
  showCustomOption = true,   // PJR-001-B: review surfaces link to master data only
  popoverClassName = '',     // e.g. a z-index override when rendered inside a high-z modal
  testIdPrefix = 'tax-collector-picker',
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState(null);
  const [linkedRow, setLinkedRow] = useState(null);
  // Full guidance payload (source / confidence / collector) for the current
  // (jurisdiction, period, category) — drives both the ★ assignment-match
  // pill inside the popover AND the truth-aware top badge below the trigger.
  const [guidance, setGuidance] = useState(null);

  // ── Debounce search input ────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  // ── Hydrate display name when linked but row not yet loaded ──────
  useEffect(() => {
    let cancelled = false;
    if (!collectorId) {
      setLinkedRow(null);
      return;
    }
    // If we already loaded this row, reuse it
    if (linkedRow && linkedRow.id === collectorId) return;
    // Fetch the exact row by ID so the green "Linked · Master Data"
    // badge survives a page reload and the trigger renders the type
    // chip + name from master data rather than just the cached string.
    (async () => {
      try {
        const row = await getTaxCollector(collectorId);
        if (!cancelled && row?.id) setLinkedRow(row);
      } catch (_) { /* silent — trigger will fall back to payeeName */ }
    })();
    return () => { cancelled = true; };
  }, [collectorId, linkedRow]);

  // ── Resolve the Phase-3 assignment (used for BOTH the ★ pill in the
  //    popover AND the badge above the trigger). Runs whenever the
  //    (jurisdiction, period, category) tuple changes — NOT gated on
  //    popover open — so the badge can render the truthful state even
  //    when the picker is closed.
  //
  //    Feb 7, 2026 — Context fallback. When the parcel row has no
  //    `jurisdiction_id` FK saved (legacy data drift), the resolver is
  //    still consulted when `jurisdictionName + state` are supplied so
  //    a formal assignment surfaced in Admin → Jurisdiction Directory
  //    is not missed. The backend echoes the matched master id in
  //    `resolved_jurisdiction_id`; we lift it to a ref so the click
  //    handler can persist the FK back onto the parcel/order row.
  const resolvedJurIdRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    if ((state || '').toUpperCase() !== 'PA') {
      setGuidance(null);
      resolvedJurIdRef.current = null;
      return;
    }
    // Need EITHER a jurisdiction FK OR enough context to resolve one.
    const hasContext = !!(jurisdictionName && state);
    if (!jurisdictionId && !hasContext) {
      setGuidance(null);
      resolvedJurIdRef.current = null;
      return;
    }
    (async () => {
      try {
        const refKey = `${jurisdictionId || `ctx:${state}:${jurisdictionType || ''}:${jurisdictionName || ''}`}|${taxPeriod}|${taxCategoryCode || ''}|${municipalityJurisdictionId || ''}`;
        const res = await resolveCollectorGuidance([{
          ref_id: refKey,
          jurisdiction_id: jurisdictionId || null,
          tax_period: taxPeriod,
          tax_category_code: taxCategoryCode || null,
          // Context fallback fields — backend uses these only when
          // jurisdiction_id is null.
          state: state || null,
          jurisdiction_name: jurisdictionName || null,
          jurisdiction_type: jurisdictionType || null,
          // Feb 2026 (Option C) — municipality scope hint. Optional.
          municipality_jurisdiction_id: municipalityJurisdictionId || null,
        }]);
        const g = (res?.guidance || [])[0] || null;
        if (!cancelled) {
          setGuidance(g);
          resolvedJurIdRef.current = g?.resolved_jurisdiction_id || null;
        }
      } catch (_) {
        if (!cancelled) {
          setGuidance(null);
          resolvedJurIdRef.current = null;
        }
      }
    })();
    return () => { cancelled = true; };
  }, [jurisdictionId, state, taxPeriod, taxCategoryCode, jurisdictionName, jurisdictionType, municipalityJurisdictionId]);

  // The ★ pill on a search-result row uses the assignment-resolved collector
  // ID — but ONLY when the resolved source is an actual assignment row.
  // Legacy-fallback resolutions should not get a ★ since there's no
  // assignment to ★ against.
  const assignmentMatchId = guidance?.source === 'assignment'
    ? (guidance?.collector?.id || null)
    : null;

  // ── Search master data when the popover is open ──────────────────
  useEffect(() => {
    let cancelled = false;
    if (!open || !state) return;
    setLoading(true);
    setErrorText(null);
    (async () => {
      try {
        const data = await listTaxCollectors({
          state,
          active_only: true,
          page_size: 25,
          ...(debouncedQuery ? { search: debouncedQuery } : {}),
        });
        const list = data?.items || data?.collectors || [];
        if (!cancelled) setResults(Array.isArray(list) ? list : []);
      } catch (err) {
        if (!cancelled) {
          setErrorText(err?.response?.data?.detail || err?.message || 'Failed to load collectors');
          setResults([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, state, debouncedQuery]);

  const handlePick = useCallback((c) => {
    setLinkedRow(c);
    setOpen(false);
    setQuery('');
    // Feb 7, 2026 — Pass the resolver-matched jurisdiction id back to
    // the parent so it can persist the FK onto the parcel/order row.
    // Only meaningful when the parent had no `jurisdictionId` to begin
    // with (legacy data drift). Safe to always include — the parent
    // will no-op when the id matches what it already stored.
    const ctx = {
      resolved_jurisdiction_id: resolvedJurIdRef.current || null,
    };
    onLinkMasterData?.(c, ctx);
  }, [onLinkMasterData]);

  const handleCustom = useCallback(() => {
    setOpen(false);
    setQuery('');
    onPickCustom?.();
  }, [onPickCustom]);

  const handleUnlink = useCallback(() => {
    setLinkedRow(null);
    onUnlink?.();
  }, [onUnlink]);

  // ── Sort: ★ assignment match first, then explicitly state-scoped,
  //         then nationwide (states_served=[]) ──────────────────────
  const { explicit, nationwide } = useMemo(() => {
    const ex = [], nw = [];
    for (const c of results) {
      if (!c.states_served || c.states_served.length === 0) nw.push(c);
      else ex.push(c);
    }
    const sortAssignmentFirst = (a, b) => {
      if (a.id === assignmentMatchId) return -1;
      if (b.id === assignmentMatchId) return 1;
      return (a.display_name || '').localeCompare(b.display_name || '');
    };
    return { explicit: ex.sort(sortAssignmentFirst), nationwide: nw.sort(sortAssignmentFirst) };
  }, [results, assignmentMatchId]);

  const isLinked = !!collectorId;
  // Truth-aware badge state. The previous behavior showed the green
  // "Linked · Master Data" pill whenever ANY collectorId was set — even
  // if no formal assignment backed it. That conflated "FK is set" with
  // "Option-A assignment is wired". This component now distinguishes:
  //
  //   • 'assignment_match' — collectorId matches the active assignment for
  //     (jurisdiction, period, category). Truly Option-A linked. Green.
  //   • 'manual'           — collectorId is set but no matching active
  //     assignment exists. Local pick (or stale assignment). Amber.
  //   • 'none'             — no collectorId set. No badge.
  const badgeState = !isLinked
    ? 'none'
    : (assignmentMatchId && assignmentMatchId === collectorId
        ? (guidance?.ambiguous ? 'ambiguous' : 'assignment_match')
        : 'manual');
  // Feb 6, 2026 — When the collector is resolved via an Option-A assignment,
  // lock the picker to read-only on the Fulfillment surface. Processors
  // must consume the Admin-maintained assignment, not override it at the
  // order level. To change the collector for an assignment-resolved row,
  // Admin edits go through Jurisdiction Directory → Collectors & Assignments
  // or the Tax Collector Directory. Manual (no assignment) and unlinked
  // states keep the existing pick-by-search behavior.
  const isAssignmentLocked = badgeState === 'assignment_match';
  const effectivelyDisabled = disabled || isAssignmentLocked;
  // Feb 2026 (Bullskin/Leslie Wiltrout UAT) — Trigger label MUST prefer the
  // collector's `make_checks_payable_to` over `display_name`. The Fulfillment
  // "Make Checks Payable To" field is a payment instruction, not a directory
  // identifier: when Admin sets a personal payee (e.g. "LESLIE WILTROUT")
  // the field must render that literal string to stay consistent with the
  // Jurisdiction Directory's Check Payable Name, Mail-Away letters, Order
  // Summary, and generated PDFs. The prior order (`display_name` first)
  // caused the field to say "BULLSKIN TOWNSHIP" — the directory name of
  // the collector, NOT the payee — which would misprint on checks.
  //
  // Precedence:
  //   1. linkedRow.make_checks_payable_to (Admin-configured payable name)
  //   2. linkedRow.display_name           (collector directory identifier — legacy fallback)
  //   3. payeeName                        (order-level free-text — for non-linked/manual rows)
  const _linkedPayable = (linkedRow?.make_checks_payable_to || '').trim();
  const triggerLabel = _linkedPayable
    || linkedRow?.display_name
    || payeeName
    || '';
  const triggerType = linkedRow?.collector_type;
  // Chip precedence: matched assignment category (assignment-resolved
  // states) → collector master type (manual/legacy states).
  const chipLabel = ((badgeState === 'assignment_match' || badgeState === 'ambiguous')
    && guidance?.category_matched && CATEGORY_LABEL[guidance.category_matched])
    || TYPE_LABEL[triggerType] || triggerType;
  const periodBadgeClass = taxPeriod === 'delinquent'
    ? 'bg-blue-50 text-blue-700 border-blue-200'
    : 'bg-emerald-50 text-emerald-700 border-emerald-200';

  return (
    <div className="space-y-1" data-testid={`${testIdPrefix}-root`}>
      {/* Truth-aware badge above the trigger. Renders different copy and
          color depending on whether the linked collector is backed by an
          active Option-A assignment OR is just a manual local pick. */}
      {badgeState === 'assignment_match' && (
        <div className="flex items-center justify-between">
          <span
            className={`tcp-fade-in tcp-anim inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${periodBadgeClass}`}
            data-testid={`${testIdPrefix}-linked-badge`}
            title="An active assignment matching this jurisdiction, period and category points at this collector. To change the collector, edit the assignment in Admin → Jurisdiction Directory."
          >
            <Check className="w-3 h-3" />
            Linked · Assignment
          </span>
          {/* Feb 6, 2026 — "Unlink & edit manually" intentionally REMOVED
              from the assignment_match state. Processors cannot override
              an Admin-maintained assignment at the order level. Use
              Admin → Jurisdiction Directory → Collectors & Assignments,
              or the Tax Collector Directory, to change the linkage. */}
          <span
            className="sr-only"
            data-testid={`${testIdPrefix}-assignment-locked-hint`}
            title="Managed in Admin → Jurisdiction Directory"
          >
            managed in Admin
          </span>
        </div>
      )}
      {badgeState === 'ambiguous' && (
        <div className="flex items-center justify-between">
          <span
            className="tcp-fade-in tcp-anim inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-amber-50 text-amber-800 border-amber-200"
            data-testid={`${testIdPrefix}-ambiguous-badge`}
            title={`Multiple equally ranked collector assignments were found for this jurisdiction${
              (guidance?.tied_collectors || []).length
                ? ` (also eligible: ${(guidance.tied_collectors || []).map((t) => t.collector_name).filter(Boolean).join(', ')})`
                : ''
            }. Add the applicable municipality jurisdiction to the certificate to resolve the correct scoped collector.`}
          >
            <AlertTriangle className="w-3 h-3" />
            Ambiguous — needs review
          </span>
          <span
            className="text-[10px] text-amber-600 italic"
            data-testid={`${testIdPrefix}-ambiguous-hint`}
          >
            add the municipality jurisdiction to resolve
          </span>
        </div>
      )}
      {badgeState === 'manual' && (
        <div className="flex items-center justify-between">
          <span
            className="tcp-fade-in tcp-anim inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-amber-50 text-amber-800 border-amber-200"
            data-testid={`${testIdPrefix}-manual-badge`}
            title="No active assignment matches this jurisdiction, period and category. This collector was picked manually for this order."
          >
            <Pencil className="w-3 h-3" />
            Manual selection · No formal assignment
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={handleUnlink}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline flex items-center gap-1"
              data-testid={`${testIdPrefix}-unlink-btn`}
            >
              <Pencil className="w-3 h-3" /> Unlink &amp; edit manually
            </button>
          )}
        </div>
      )}
      {/* TITLEFLOW-DELINQUENT-COLLECTOR-SUGGESTION-001 — a formal assignment exists for this
          (jurisdiction, period, category) but the order holds a different manual pick.
          Never auto-adopt: the processor must click "Use it" (reuses handlePick → onLinkMasterData). */}
      {badgeState === 'manual' && assignmentMatchId && guidance?.collector && !guidance?.ambiguous && (
        <div
          className="tcp-fade-in mt-1 flex items-center gap-2 rounded-lg border border-emerald-200/70 bg-emerald-50/60 px-2 py-1 text-[11px] text-emerald-900"
          data-testid={`${testIdPrefix}-assignment-suggestion`}
          role="status"
        >
          <Star className="w-3 h-3 flex-shrink-0 fill-emerald-500 text-emerald-500" />
          <span className="truncate">
            Verified collector available: <strong data-testid={`${testIdPrefix}-assignment-suggestion-name`}>{guidance.collector.display_name}</strong>
          </span>
          {!disabled && (
            <button
              type="button"
              onClick={() => handlePick(guidance.collector)}
              className="tcp-anim ml-auto flex-shrink-0 rounded-full bg-emerald-600 px-2.5 py-0.5 text-[11px] font-semibold text-white hover:bg-emerald-700 active:scale-95"
              data-testid={`${testIdPrefix}-assignment-suggestion-use-btn`}
            >
              Use it
            </button>
          )}
        </div>
      )}

      <Popover open={open} onOpenChange={effectivelyDisabled ? undefined : setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={effectivelyDisabled}
            className={`w-full h-8 px-2.5 text-sm flex items-center justify-between rounded-md border transition ${
              effectivelyDisabled
                ? 'bg-slate-50 border-slate-200 text-slate-700 cursor-not-allowed'
                : 'bg-white border-slate-300 hover:border-slate-400'
            }`}
            data-testid={`${testIdPrefix}-trigger`}
            aria-readonly={isAssignmentLocked || undefined}
            title={isAssignmentLocked
              ? 'Managed by the jurisdiction assignment. Edit in Admin → Jurisdiction Directory to change.'
              : undefined}
          >
            <span className="flex items-center gap-2 min-w-0">
              {isLinked && (chipLabel || triggerType) && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200 shrink-0" data-testid={`${testIdPrefix}-category-chip`}>
                  {chipLabel}
                </span>
              )}
              <span className={`truncate ${triggerLabel ? 'text-slate-900' : 'text-slate-400'}`}>
                {triggerLabel || 'Search or select a Tax Collector…'}
              </span>
            </span>
            {/* Hide the dropdown chevron when locked — it falsely implies
                the picker is interactive. Show a small lock indicator
                instead so the read-only state is unambiguous. */}
            {isAssignmentLocked ? (
              <Lock className="w-3.5 h-3.5 text-slate-300 shrink-0" aria-hidden="true" title="Managed by jurisdiction assignment" />
            ) : (
              <ChevronsUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          align="start"
          className={`w-[460px] p-0 max-w-[calc(100vw-32px)] ${popoverClassName}`}
          data-testid={`${testIdPrefix}-popover`}
        >
          {/* Search input */}
          <div className="p-2 border-b border-slate-100">
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${state || ''} tax collectors…`}
              className="h-8 text-sm"
              data-testid={`${testIdPrefix}-search-input`}
            />
          </div>

          {/* Result body */}
          <div className="max-h-72 overflow-auto" data-testid={`${testIdPrefix}-results`}>
            {loading && (
              <div className="px-3 py-3 text-xs text-slate-500 flex items-center gap-2" data-testid={`${testIdPrefix}-loading`}>
                <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading collectors…
              </div>
            )}
            {!loading && errorText && (
              <div className="px-3 py-3 text-xs text-red-600" data-testid={`${testIdPrefix}-error`}>
                {errorText}
              </div>
            )}
            {!loading && !errorText && explicit.length === 0 && nationwide.length === 0 && (
              <div className="px-3 py-3 text-xs text-slate-500" data-testid={`${testIdPrefix}-empty`}>
                No matches. Try a different search{showCustomOption ? ' or pick "Type a custom payee"' : ''}.
              </div>
            )}

            {!loading && !errorText && explicit.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50 border-b border-slate-100">
                  {state} Tax Collectors · {explicit.length}
                </div>
                {explicit.map((c) => (
                  <CollectorRow
                    key={c.id}
                    collector={c}
                    isAssignmentMatch={c.id === assignmentMatchId}
                    isSelected={c.id === collectorId}
                    onPick={handlePick}
                    testIdPrefix={testIdPrefix}
                  />
                ))}
              </>
            )}

            {!loading && !errorText && nationwide.length > 0 && (
              <>
                <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50 border-y border-slate-100">
                  Nationwide collectors · {nationwide.length}
                </div>
                {nationwide.map((c) => (
                  <CollectorRow
                    key={c.id}
                    collector={c}
                    isAssignmentMatch={c.id === assignmentMatchId}
                    isSelected={c.id === collectorId}
                    onPick={handlePick}
                    showNationwidePill
                    testIdPrefix={testIdPrefix}
                  />
                ))}
              </>
            )}
          </div>

          {/* Footer: free-text fallback + admin link */}
          <div className="border-t border-slate-200 bg-slate-50">
            {showCustomOption && (
            <button
              type="button"
              onClick={handleCustom}
              className="w-full text-left px-3 py-2 hover:bg-slate-100 flex items-center gap-2 text-[13px] text-slate-700"
              data-testid={`${testIdPrefix}-custom-btn`}
            >
              <Plus className="w-3.5 h-3.5 text-slate-500" />
              <span>
                Type a custom payee
                <span className="text-slate-400 italic"> — for one-off entries (no master-data link)</span>
              </span>
            </button>
            )}
            <a
              href="/admin/jurisdictions/tax-collectors"
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 flex items-center gap-1.5 text-[11px] text-indigo-700 hover:underline"
              data-testid={`${testIdPrefix}-admin-link`}
            >
              <ExternalLink className="w-3 h-3" />
              Can't find them? Open Admin → Tax Collectors to add a new one
            </a>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};

const CollectorRow = ({ collector, isAssignmentMatch, isSelected, onPick, showNationwidePill, testIdPrefix }) => {
  const phone = collector.phone || '';
  const addrParts = [collector.physical_address, collector.mailing_address].filter(Boolean);
  const addr = addrParts[0] ? addrParts[0].split('\n').filter(Boolean).join(' · ') : '';
  // Feb 2026 — operational defaults chips. Read directly from the
  // collector row; jurisdiction overrides apply only after pick at the
  // JurisdictionManager / MailAwayModal layer.
  const fee     = collector.default_mlc_fee;
  const eta     = collector.default_eta_days;
  const sase    = collector.default_sase_required;
  const hasInstr = !!(collector.request_instructions && String(collector.request_instructions).trim());
  const feeChip   = (fee != null && fee > 0)
    ? <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-violet-50 text-violet-700 border border-violet-200">Fee&nbsp;${Number(fee).toFixed(0)}</span>
    : <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-rose-50 text-rose-700 border border-rose-200">No fee default</span>;
  return (
    <button
      type="button"
      onClick={() => onPick(collector)}
      className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-l-2 ${
        isAssignmentMatch ? 'border-emerald-400 bg-emerald-50/40' : 'border-transparent'
      } ${isSelected ? 'bg-indigo-50/50' : ''}`}
      data-testid={`${testIdPrefix}-row-${collector.id}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 space-y-0.5">
          <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">
            {collector.display_name}
            {isSelected && <Check className="w-3.5 h-3.5 text-indigo-600 shrink-0" />}
          </div>
          <div className="text-[11px] text-slate-500 truncate">
            {[addr, phone].filter(Boolean).join(' · ') || (collector.legal_name || '')}
          </div>
          {/* Operational defaults chips — Feb 2026 */}
          <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
            {feeChip}
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
              ETA&nbsp;{eta != null ? `${eta}d` : '—'}
            </span>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
              SASE&nbsp;{sase === true ? 'Yes' : sase === false ? 'No' : '—'}
            </span>
            {hasInstr && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                📝 Instructions
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1 shrink-0">
          {collector.collector_type && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200">
              {TYPE_LABEL[collector.collector_type] || collector.collector_type}
            </span>
          )}
          {showNationwidePill && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
              Nationwide
            </span>
          )}
          {isAssignmentMatch && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 flex items-center gap-0.5">
              <Star className="w-2.5 h-2.5" /> Assignment match
            </span>
          )}
        </div>
      </div>
    </button>
  );
};

export default TaxCollectorPicker;
